/** @module application */

import $ from 'jquery';
import { liveEvents } from './index.js';

let autoScrollingInstance = null;

/**
 * Sets up all automatic scrolling happening on the page.
 *
 * @param {object} [options={}] - Initial options.
 * @param {int|string|function|object} [options.offset] - Defines offsets to apply to scrolling upwards and downwards, optionally for different breakpoints. If not of type <code>object</code>, settings apply to both directions and any breakpoint; see {@link options.offset.up} for options.
 * @param {int|string|function} [options.offset.up='.header'] - The offset - or an element to measure the offset from, or a callback returning the offset - to apply when scrolling upwards. Applies to all breakpoints.
 * @param {int|string|function} [options.offset.down=0] - The offset - or an element to measure the offset from, or a callback returning the offset - to apply when scrolling downwards. Applies to all breakpoints.
 * @param {int|string|function} [options.offset.<breakpoint>] - Offset-settings for specific breakpoints (as defined in resources/scss/ __vars/_breakpoints.scss). See {@link options.offset} for options.
 * @param {boolean} [options.fromHashChanges=true] - Whether updates of the hash should trigger scrolling.
 * @param {boolean} [options.fromAnchors=true] - Whether clicks on anchors should scroll to the target element.
 * @param {boolean} [options.updateHash=false] - Whether clicks on anchors should update the hash value of the URL. This setting can be by-passed by setting data-attribute <code>data-set-hash</code> to <code>true|false</code> on the particular anchor.
 * @param {function(string):string} [options.getTargetId] - An optional function returning the value of the id-attribute of the element to which scrolling should happen. Receives the current hash.
 * @param {string|string[]|HTMLElement|HTMLElement[]|NodeList} [options.scrollAnchors='a&#91;href*="#"&#93;:not(&#91;href="#"&#93;):not(.no-scroll)'] - One or more elements or selectors for selecting the anchors to animate scrolling from
 * @param {boolean} [options.extendDefaultAnchorsSelector=false] - Whether to extend or overwrite the default {@link options.scrollAnchors}
 *
 * @example
 * // offset as integer:
 * new AutoScrolling({
 *   offset: 50,
 * });
 *
 * @example
 * // offset as integer for different breakpoints:
 * new AutoScrolling({
 *     offset: {
 *         small: 50,
 *         desktop: 150,
 *     },
 * });
 *
 * @example
 * // different offsets for different directions, for all breakpoints, as integer and selector:
 * new AutoScrolling({
 *     offset: {
 *         up: '.header',
 *         down: 150,
 *     },
 * });
 *
 * @example
 * // different offsets for different directions, and different breakpoints, as integer, selector and function:
 * new AutoScrolling({
 *     offset: {
 *         small: 0,
 *         medium: {
 *             up: '.header',
 *             down: 150,
 *         },
 *         page() {
 *             return 75;
 *         }
 *     },
 * });
 */
export class AutoScrolling {
    constructor(options = {}) {
        this.defaultOptions = {
            offset: 0,
            fromHashChanges: true,
            fromAnchors: true,
            updateHash: false,
            getTargetId: undefined,
            scrollAnchors: '',
            extendDefaultAnchorsSelector: false,
            beforeScroll() {},
        };

        this.options = { ...this.defaultOptions, ...options, elOffset: {} };

        this._init();

        autoScrollingInstance = this;
    }

    static singleton(options = {}) {
        if (autoScrollingInstance === null) {
            new AutoScrolling(options);
        }

        return autoScrollingInstance;
    }

    _init() {
        this._initOffset();

        if (this.options.fromHashChanges) {
            this._initHashScrolling();
        }

        if (this.options.fromAnchors) {
            this._initAnchorScrolling();
        }
    }

    _initOffset() {
        let offset = this.options.offset;

        if (typeof offset !== 'object') {
            offset = this.options.offset = {
                small: {
                    up: offset,
                    down: offset,
                },
            };
        } else if ('up' in offset || 'down' in offset) {
            offset = this.options.offset = {
                small: offset,
            };
        }

        for (const breakpoint in offset) {
            if (Object.prototype.hasOwnProperty.call(offset, breakpoint)) {
                let val = offset[breakpoint];

                if (typeof val !== 'object') {
                    offset[breakpoint] = {
                        up: Number(val),
                        down: Number(val),
                    };
                } else {
                    offset[breakpoint] = {
                        up: isNaN(val.up) ? document.querySelector(val.up) : Number(val.up),
                        down: isNaN(val.down) ? document.querySelector(val.down) : Number(val.down),
                    };
                }
            }
        }

        this.options.offset = offset;
    }

    _initEvents() {
        window.addEventListener('popstate', this._onUrlChange.bind(this));
    }

    _initHashScrolling() {
        this._initEvents();

        if (CONFIG.initialHash) {
            location.hash = CONFIG.initialHash.replace(/^#\/?(.*)/, '#$1');
        }
    }

    _onScrollAnchorActivation(event, matchingSelector) {
        let target = event.target.closest(matchingSelector),
            href = target.href,
            currentPathName = window.location.pathname.replace(/\/$/, ''),
            hrefPathName = href
                .replace(/^.*\/\/[^/]+/, '')
                .replace(/#.*/, '')
                .replace(/\/$/, '');

        // get the actual hash of the href
        if (href && href.indexOf('#') !== -1) {
            href = href.split('#').pop();
        }

        if (
            this.options.fromHashChanges &&
            ((this.options.updateHash && target.dataset.setHash !== 'false') ||
                (!this.options.updateHash && target.dataset.setHash === 'true'))
        ) {
            event.preventDefault(); // do not allow setting the hash, as some browser move directly to the target, before the popstate event is triggered
            // mask the hash so that browsers don't navigate
            if (href && href[0] !== '/') {
                href = '/' + href;
            }

            if (currentPathName !== hrefPathName) {
                window.location.href = target.href; // change url if anchor is on another page
            } else {
                location.hash = href; // set hash explicitly to trigger the popstate event, on which scrolling depends
            }
        } else if (currentPathName === hrefPathName) {
            event.preventDefault();
            this.scroll(this._getTargetSelector(href));
        }
    }

    async _initAnchorScrolling() {
        const scrollAnchors = this.options.scrollAnchors,
            extendDefaultAnchorsSelector = this.options.extendDefaultAnchorsSelector,
            defaultSelector = 'a[href*="#"]:not([href="#"]):not(.no-scroll)',
            selector = [extendDefaultAnchorsSelector ? defaultSelector : null, scrollAnchors || defaultSelector].filter(
                Boolean,
            );

        if (!this.options.fromHashChanges) {
            this._initEvents();
        }

        (await liveEvents())
            .addEventListener(selector, 'click', this._onScrollAnchorActivation.bind(this))
            .addEventListener(selector, 'keydown', (event, matchingSelector) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    // enter and space key should act the same
                    this._onScrollAnchorActivation(event, matchingSelector);
                    event.stopImmediatePropagation(); // do not trigger a click event
                }
            });
    }

    _onUrlChange(event) {
        if (this.options.fromHashChanges !== true && this.options.fromAnchors) {
            this._onUrlChangeWithAnchorScrolling(event);
        } else {
            this._onUrlChangeWithHashScrolling(event);
        }
    }

    _onUrlChangeWithHashScrolling(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        let selector = this._getTargetSelector(location.hash),
            target;

        try {
            target = document.querySelectorAll(selector);
        } catch (e) {
            target = null;
        }

        if (!target) {
            return;
        }

        if (document.readyState === 'complete') {
            this.scroll(selector);
        } else {
            document.addEventListener('readystatechange', () => {
                if (document.readyState === 'complete') {
                    this.scroll(selector);
                }
            });
        }
    }

    _getTargetSelector(hash) {
        let options = this.options,
            attributeValue;

        if (typeof options.getTargetId === 'function') {
            attributeValue = options.getTargetId(hash);
        } else {
            attributeValue = hash.replace(/^#/, '');
        }

        return '#' + attributeValue;
    }

    _onUrlChangeWithAnchorScrolling(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
    }

    scroll(selector, offset = 0, withFocus = true) {
        let $target = $(selector),
            targetOffset = $target.data('scroll-offset'); // eslint-disable-line jquery/no-data

        if (targetOffset && isNaN(targetOffset)) {
            targetOffset = document.querySelector(targetOffset).scrollHeight;
        }

        this.options.beforeScroll($target.get(0), $target);

        return new Promise((resolve) => {
            Foundation.SmoothScroll.scrollToLoc(
                $target,
                {
                    threshold: 0,
                    offset: this._getScrollOffset(selector) + (targetOffset || 0) + offset,
                    animationDuration: 300,
                },
                () => {
                    if (withFocus) {
                        // a11y: focus needs to be moved, too
                        let el = typeof selector === 'string' ? document.querySelector(selector) : selector;

                        el.focus({ preventScroll: true });

                        if (document.activeElement !== el) {
                            let listenerOptions = { once: true, passive: true },
                                listener = () => {
                                    el.removeAttribute('tabindex');
                                };

                            // add tabindex to make focusable and remove again
                            el.tabIndex = -1;
                            el.addEventListener('blur', listener, listenerOptions);
                            el.addEventListener('focusout', listener, listenerOptions);

                            el.focus({ preventScroll: true });
                        }
                    }

                    resolve();
                },
            );
        });
    }

    _getScrollOffset(selector) {
        let options = this.options,
            offset = 0,
            target = typeof selector === 'string' ? document.querySelector(selector) : selector,
            dir;

        if (!target) {
            return offset;
        }

        dir = target.offsetTop < Math.max(document.documentElement.scrollTop, document.body.scrollTop) ? 'up' : 'down';

        for (const query of [...Foundation.MediaQuery.queries].reverse()) {
            if (query.name in options.offset && Foundation.MediaQuery.atLeast(query.name)) {
                let offsetConfig = options.offset[query.name][dir];

                if (typeof offsetConfig === 'function') {
                    offset = offsetConfig();
                } else if (typeof offsetConfig === 'object') {
                    offset = offsetConfig.getBoundingClientRect().height;
                } else {
                    offset = offsetConfig;
                }

                break;
            }
        }

        return offset;
    }
}
